import { hasAnyPermission, type Permission, type Role } from '@fleetcore/shared'
import {
  BarChart3,
  Bell,
  ClipboardCheck,
  PackageCheck,
  Snowflake,
  Truck,
  CircleDot,
  FileCheck,
  FileText,
  Globe2,
  Fuel,
  Gauge,
  HardDrive,
  HardHat,
  LayoutDashboard,
  Map,
  Navigation,
  PackageOpen,
  Route,
  Settings,
  Shapes,
  TrendingUp,
  User,
  Wrench,
  type LucideIcon,
} from 'lucide-react'

export const NAV_SECTIONS = ['operations', 'analysis', 'administration'] as const
export type NavSection = (typeof NAV_SECTIONS)[number]

export interface NavItem {
  /** Resolves to `nav:items.<key>` and `nav:hints.<key>`. */
  key: string
  path: string
  icon: LucideIcon
  section: NavSection
  /** Holding any one of these reveals the entry. */
  permissions: readonly Permission[]
}

/**
 * Route paths stay in English even though the UI defaults to French.
 *
 * A localized URL set would mean two routing tables, two sets of bookmarks and a
 * redirect whenever someone switches language mid-session. The address bar is
 * not a translated surface; everything the user reads still comes from the
 * catalog.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    // The platform operator's view of every organisation's vehicles. Only super_admin
    // holds `platform:manage_tenants`, so no customer account ever sees this entry.
    key: 'platform',
    path: '/platform',
    icon: Globe2,
    section: 'operations',
    permissions: ['platform:manage_tenants'],
  },
  {
    key: 'dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    section: 'operations',
    permissions: ['dashboards:read'],
  },
  { key: 'map', path: '/map', icon: Map, section: 'operations', permissions: ['live:read'] },
  {
    key: 'trips',
    path: '/trips',
    icon: Navigation,
    section: 'operations',
    permissions: ['trips:read'],
  },
  {
    key: 'alerts',
    path: '/alerts',
    icon: Bell,
    section: 'operations',
    permissions: ['alerts:read'],
  },
  {
    key: 'self',
    path: '/my-trips',
    icon: User,
    section: 'operations',
    // The driver self-service view. Only `driver` holds self:read, so no other
    // role sees this entry — and a driver sees nothing else.
    permissions: ['self:read'],
  },

  { key: 'fuel', path: '/fuel', icon: Fuel, section: 'analysis', permissions: ['fuel:read'] },
  {
    key: 'driving',
    path: '/driving',
    icon: Gauge,
    section: 'analysis',
    permissions: ['scoring:read'],
  },
  {
    // Orders are the COMMERCIAL layer above planning, so they share `routes:*` with
    // it: the dispatcher who plans a round is the person who took the order that
    // produced it.
    key: 'orders',
    path: '/orders',
    icon: PackageCheck,
    section: 'operations',
    permissions: ['routes:read'],
  },
  {
    // Trailers are fleet, so they share `vehicles:read` with the vehicle list. The
    // screen's job is the idle ones: a trailer nobody has hitched for three weeks is
    // capital earning nothing, and it is invisible everywhere else in the product.
    key: 'trailers',
    path: '/trailers',
    icon: Truck,
    section: 'operations',
    permissions: ['vehicles:read'],
  },
  {
    // The cold chain sits in OPERATIONS, not analysis. A dispatcher looks at this to
    // decide whether the pallet on a lorry currently warming up can still be delivered
    // — which is a decision about this afternoon, not a report about last month.
    key: 'coldChain',
    path: '/cold-chain',
    icon: Snowflake,
    section: 'operations',
    permissions: ['maintenance:read'],
  },
  {
    key: 'maintenance',
    path: '/maintenance',
    icon: Wrench,
    section: 'analysis',
    permissions: ['maintenance:read'],
  },
  {
    // Papers and oil changes for the whole fleet. Same permission as the board: the
    // person who books a vidange is the person who renews an assurance.
    key: 'papers',
    path: '/maintenance/papers',
    icon: FileCheck,
    section: 'analysis',
    permissions: ['maintenance:read'],
  },
  {
    // The workshop's walk-around board. Shares `maintenance:read` with the rest of the
    // atelier surfaces: the person who books a vidange is the person who reads these.
    key: 'inspections',
    path: '/inspections',
    icon: ClipboardCheck,
    section: 'operations',
    permissions: ['maintenance:read'],
  },
  {
    // The driver's own walk-around. `self:inspect` is held by `driver` alone, so this
    // entry is invisible to every other role — and it is the second of the two entries
    // a driver can see at all.
    key: 'myInspection',
    path: '/my-inspection',
    icon: ClipboardCheck,
    section: 'operations',
    permissions: ['self:inspect'],
  },
  {
    // The warehouse, and the only nav entry in the product that a customer's most
    // privileged user never sees: `platform:manage_stock` is super_admin-only, because
    // this screen carries what the operator PAID for the hardware.
    key: 'stock',
    path: '/admin/stock',
    icon: PackageOpen,
    section: 'administration',
    permissions: ['platform:manage_stock'],
  },
  {
    // The operator's own equipment: trackers, SIM cards, installation records and
    // call-outs. Shares `devices:read` for the reason the route gives — the person who
    // registers a tracker is the person who records where it was fitted, and a
    // permission of its own would appear for nobody until it was granted by hand.
    key: 'hardware',
    path: '/hardware',
    icon: HardDrive,
    section: 'administration',
    permissions: ['devices:read'],
  },
  {
    // Suivi de pneumatique shares `maintenance:read`: the person who books a vidange is
    // the person who orders tyres, and a permission of its own would have to be granted
    // to every existing fleet_manager before the entry appeared for anybody.
    key: 'tyres',
    path: '/tyres',
    icon: CircleDot,
    section: 'analysis',
    permissions: ['maintenance:read'],
  },
  {
    /**
     * `scoring:read` alone gates the ENTRY, while the endpoint requires `fuel:read`
     * too. Deliberate: hiding the module from somebody who holds one of the two would
     * leave them with no way to discover it exists, and the server still refuses the
     * call. A nav entry is a signpost, not a lock.
     */
    key: 'predictions',
    path: '/predictions',
    icon: TrendingUp,
    section: 'analysis',
    permissions: ['scoring:read'],
  },
  {
    // Rapport flotte: every vehicle's distance, fuel, idling and refills over a period,
    // as charts and a table. A report, so `reports:read`, beside the report library.
    key: 'fleetReport',
    path: '/reports/fleet',
    icon: BarChart3,
    section: 'analysis',
    permissions: ['reports:read'],
  },
  {
    key: 'reports',
    path: '/reports',
    icon: FileText,
    section: 'analysis',
    permissions: ['reports:read'],
  },

  {
    key: 'geofences',
    path: '/zones',
    icon: Shapes,
    section: 'administration',
    permissions: ['geofences:read'],
  },
  {
    key: 'routes',
    path: '/routes',
    icon: Route,
    section: 'administration',
    permissions: ['routes:read'],
  },
  {
    key: 'worksites',
    path: '/worksites',
    icon: HardHat,
    section: 'administration',
    permissions: ['worksites:read'],
  },
  {
    key: 'admin',
    path: '/admin',
    icon: Settings,
    section: 'administration',
    // `settings:read` is deliberately absent: a viewer holds it and must not see
    // the administration section. Every listed permission stops at tenant_admin.
    permissions: ['users:read', 'settings:write', 'apikeys:manage', 'audit:read'],
  },
]

export function filterNavItems(roles: readonly Role[]): NavItem[] {
  return NAV_ITEMS.filter((item) => hasAnyPermission(roles, item.permissions))
}

/**
 * The ONE entry to highlight: the longest path the address starts with. A plain prefix
 * match lit "Rapports" on /reports/fleet and "Entretien" on /maintenance/papers, next
 * to the entry actually open; a plain exact match would light nothing on a sub-page
 * such as /maintenance/vehicles/:id.
 */
export function activeNavPath(pathname: string, items: readonly NavItem[]): string | null {
  let best: string | null = null
  for (const item of items) {
    const matches = pathname === item.path || pathname.startsWith(`${item.path}/`)
    if (matches && (best === null || item.path.length > best.length)) best = item.path
  }
  return best
}

export interface NavGroup {
  section: NavSection
  items: NavItem[]
}

/** Sections with no visible entry are dropped rather than rendered empty. */
export function groupNavItems(items: readonly NavItem[]): NavGroup[] {
  return NAV_SECTIONS.map((section) => ({
    section,
    items: items.filter((item) => item.section === section),
  })).filter((group) => group.items.length > 0)
}

/**
 * Where "/" should land for this user. A driver has no dashboard permission, so
 * a hardcoded /dashboard redirect would drop them straight onto a 403.
 */
export function defaultRouteFor(roles: readonly Role[]): string {
  // The platform operator belongs to no organisation: every per-organisation screen is
  // empty for them, so they land on the one that shows every organisation at once.
  return filterNavItems(roles)[0]?.path ?? '/dashboard'
}
